import { FC, useState } from 'react';
import BaseModal from '@app/shared/components/modal/BaseModal';
import { ModalSize } from '@app/shared/components/modal/base-modal-enums';
import { useAppDispatch } from '@core/hooks/redux-hooks';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance } from '@app/shared/components/button/button-enums';
import { ProjectDto } from '@app/shared/models/contracts/project-dto';
import { saveNewProject } from '@core/store/project-list-slice';
import { useNavigate } from 'react-router-dom';
import styles from './import-project-modal.module.scss';
import { useLocale } from '@app/core/hooks/useLocale';

interface ImportProjectModalProps {
  setIsOpen: (value: boolean) => void;
  isOpen: boolean;
}

const ImportProjectModal: FC<ImportProjectModalProps> = ({ isOpen, setIsOpen }): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { l } = useLocale();
  const [jsonInput, setJsonInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
    setJsonInput('');
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      // Parse the JSON
      const projectData: ProjectDto = JSON.parse(jsonInput);

      // Submit to API
      const resultAction = await dispatch(saveNewProject(projectData));

      if (saveNewProject.fulfilled.match(resultAction)) {
        const projectId = resultAction.payload;
        handleClose();
        // Navigate to the newly created project
        navigate(`/projects/${projectId}`);
      } else {
        throw new Error(l('_FailedToCreateProject'));
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : l('_InvalidJsonFormat');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseModal
      size={ModalSize.Medium}
      title={l('_ImportProjectFromJson')}
      isOpen={isOpen}
      onRequestClose={handleClose}
      onCloseButtonClick={handleClose}>
      <div className={styles['import-project']}>
        <p className={styles['import-project__description']}>{l('_ImportProjectDescription')}</p>

        <textarea
          className={styles['import-project__textarea']}
          value={jsonInput}
          onChange={(e) => setJsonInput(e.target.value)}
          placeholder='{"name": "My Project", ...}'
          rows={15}
          disabled={isSubmitting}
        />

        {error && <div className={styles['import-project__error']}>{error}</div>}

        <div className={styles['import-project__actions']}>
          <Button
            appearance={ButtonAppearance.DEFAULT_PRIMARY}
            onClick={handleSubmit}
            disabled={!jsonInput.trim() || isSubmitting}>
            {isSubmitting ? l('_Importing') : l('_ImportProject')}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

export default ImportProjectModal;
